import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { fmtNumber, useT } from '@/i18n'
import { inventoryMessages } from '@/i18n/messages/inventory'
import { drumUrl, normalizeDrumCode } from './drumCode'
import { Qr } from './Qr'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from './shared'
import type { Drum } from './types'

const PER_SHEET = 24

/** How many fresh labels to create; then opens the print sheet with exactly those codes. */
export function LabelsDialog({ onClose }: { onClose: () => void }) {
  const t = useT(inventoryMessages)
  const navigate = useNavigate()
  const [count, setCount] = useState(String(PER_SHEET))
  const [error, setError] = useState<string | null>(null)
  const n = Number(count)
  const valid = Number.isInteger(n) && n >= 1 && n <= 240
  const create = useMutation({
    mutationFn: () => api.post<{ codes: string[] }>('/api/inventory/labels', { count: n }),
    onSuccess: (r) => { onClose(); navigate(`/inventory/labels?codes=${r.codes.join(',')}`) },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  })
  return (
    <Dialog title={t('labelsTitle')} hint={t('labelsHint')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton>
        <PrimaryButton disabled={!valid || create.isPending} onClick={() => create.mutate()}>{t('labelsCreate')}</PrimaryButton></>}>
      <Field label={t('labelsCount')} hint={valid ? t('labelsSheets', { count: Math.ceil(n / PER_SHEET) }) : undefined}>
        <input inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value)} className={`${INPUT} max-w-[8rem]`} />
      </Field>
      {error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('couldNotSave', { error })}</p>}
    </Dialog>
  )
}

/**
 * A4 label sheets, 3 × 8 labels of 70 × 37 mm: QR (the drum URL), the code in large type for
 * reading without a phone, and — for drums already registered — the cable and delivered length.
 * Pre-printed labels leave a ruled line for the cable to be written on.
 */
export function LabelsPage() {
  const t = useT(inventoryMessages)
  const [params] = useSearchParams()
  const codes = (params.get('codes') ?? '').split(',').map(normalizeDrumCode).filter((c): c is string => Boolean(c))
  const drums = useQuery<Drum[]>({ queryKey: ['inv-drums'], queryFn: () => api.get<Drum[]>('/api/inventory/drums') })
  const byCode = new Map((drums.data ?? []).map((d) => [d.code, d]))
  const sheets: string[][] = []
  for (let i = 0; i < codes.length; i += PER_SHEET) sheets.push(codes.slice(i, i + PER_SHEET))

  return (
    <div className="space-y-4">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body { background: #fff !important; }
          .label-sheet { box-shadow: none !important; margin: 0 !important; break-after: page; }
          .label-sheet:last-child { break-after: auto; }
        }
      `}</style>
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <div>
          <Link to="/inventory" className="text-xs text-neutral-500 hover:underline">← {t('labelsBack')}</Link>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('labelsTitle')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-500">{t('labelsPrintHint')}</p>
        </div>
        <PrimaryButton onClick={() => window.print()} disabled={codes.length === 0}>{t('labelsPrint')}</PrimaryButton>
      </div>
      <div className="overflow-x-auto print:overflow-visible">
        {sheets.map((sheet, s) => (
          <div key={s} className="label-sheet mx-auto mb-6 bg-white text-black shadow-lg"
            style={{ width: '210mm', height: '297mm', padding: '0 0', display: 'grid',
              gridTemplateColumns: 'repeat(3, 70mm)', gridTemplateRows: 'repeat(8, 37mm)', alignContent: 'center' }}>
            {sheet.map((code) => {
              const d = byCode.get(code)
              return (
                <div key={code} style={{ display: 'flex', alignItems: 'center', gap: '2.5mm', padding: '2.5mm 3mm', overflow: 'hidden' }}>
                  <div style={{ width: '30mm', height: '30mm', flex: 'none' }}><Qr value={drumUrl(code)} size={113} title={code} /></div>
                  <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1.2mm' }}>
                    <div style={{ fontSize: '6.5pt', letterSpacing: '0.08em', color: '#555' }}>MIH · DRUM</div>
                    <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, fontSize: '12.5pt', lineHeight: 1.1, wordBreak: 'break-all' }}>{code}</div>
                    {d?.cableName ? (
                      <div style={{ fontSize: '7pt', lineHeight: 1.2 }}>
                        {d.cableName}<br />{d.lengthDeliveredM != null ? `${fmtNumber(d.lengthDeliveredM)} m` : ''}{d.provider ? ` · ${d.provider}` : ''}
                      </div>
                    ) : (
                      <div style={{ fontSize: '6.5pt', color: '#777', borderBottom: '0.3mm solid #999', paddingBottom: '3.5mm' }}>{t('labelBlank')}</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
